/**
 * PromptInspectorSheet — an opt-in, zero-footprint transparency surface that
 * shows the exact system prompt a chat session is running with, broken down by
 * the layer that contributed each part, rebuilt at read time the way the
 * reader's next turn would compile it ("Live — …").
 *
 * Clinical constraint: this must NEVER disturb bedside work. It is a single
 * subtle icon in the session header; the drawer defaults CLOSED, opens nothing
 * automatically, and adds no banner or layout shift. A clinician who never taps
 * it never sees it — and never triggers the fetch (the query is lazily enabled
 * on open; see useSessionPrompt).
 *
 * Design language (DESIGN.md): single Action Blue accent on the trigger, the
 * scale-0.95 press, bottom-sheet grammar reused from CitationSheet, utility-card
 * radius + hairline borders (no shadow — shadow is reserved for product
 * imagery), mono register for the prompt content and audit tags.
 *
 * Three data states are all handled: ERROR (retry; also a session with no
 * recorded breakdown, which the endpoint 5xxs — never a placeholder), LOADING
 * (skeleton, only once opened and with no cache), and DATA. Read-only — no
 * mutation, so no optimistic path.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { Info } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { useSessionPrompt } from '@/hooks/useSessionPrompt'

// ---------------------------------------------------------------------------
// Layer presentation table.
//
// `labelKey` → i18n key for the friendly label. `defaultExpanded` follows the
// product rule: user/patient-authored layers open by default; platform/derived
// layers stay collapsed. An unknown layer name (not in this table) is rendered
// Title-cased and collapsed by default.
//
// `sensitive` flags layers that may carry location/PHI context (patient_context)
// so the body can mark them confidential — never an alarming warning, just a
// quiet note (clinical UI: no paternalistic nags).
// ---------------------------------------------------------------------------
// ===========================================================================
// Trigger + sheet wrapper
// ===========================================================================
interface PromptInspectorSheetProps {
  sessionId: string
}

/**
 * The subtle header affordance. Renders a single ghost icon button; the drawer
 * is self-contained and defaults closed. Drop this beside the Share button.
 */
export function PromptInspectorSheet({ sessionId }: PromptInspectorSheetProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  // Lazily enabled: nothing is fetched until the drawer has been opened.
  const query = useSessionPrompt(sessionId, open)

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t('promptInspector.trigger', "What is the assistant told?")}
        className={cn(
          'w-9 h-9 flex items-center justify-center rounded-full text-primary',
          'hover:bg-primary/10 transition-colors active:scale-95 motion-reduce:transition-none',
          'min-w-[44px] min-h-[44px]',
        )}
      >
        <Info className="w-4 h-4" aria-hidden="true" />
      </button>

      <SheetContent
        side="bottom"
        className="rounded-t-2xl max-h-[85vh] flex flex-col gap-0 pb-safe"
      >
        {/* The Sheet shell owns the Radix title/description (required for the
            dialog a11y contract). The body below is context-free plain markup
            so it renders standalone from a fixture. */}
        <SheetHeader className="text-left shrink-0">
          <SheetTitle className="font-display text-lg text-neutral-900">
            {t('promptInspector.title', 'Assistant instructions')}
          </SheetTitle>
          <SheetDescription className="text-sm text-neutral-600 leading-relaxed">
            {t(
              'promptInspector.explainer',
              'These are the standing instructions the assistant follows in this conversation.',
            )}
          </SheetDescription>
        </SheetHeader>
        <PromptInspectorBody
          data={query.data}
          isLoading={query.isLoading}
          isError={query.isError}
          onRetry={() => void query.refetch()}
        />
      </SheetContent>
    </Sheet>
  )
}

// ===========================================================================
// Body — pure presentation, NO Sheet/Dialog context dependency. Rendered by the
// wired sheet above (which supplies the title + description) AND directly from a
// fixture for isolated visual dev (no network, no Sheet wrapper needed).
// ===========================================================================
interface PromptInspectorBodyProps {
  data?: PromptPreviewResponse
  isLoading: boolean
  isError: boolean
  onRetry: () => void
}

import type { PromptPreviewResponse } from '@/api/types_session'

export function PromptInspectorBody({
  data,
  isLoading,
  isError,
  onRetry,
}: PromptInspectorBodyProps) {
  const { t } = useTranslation()

  // ERROR — no cache to fall back on. Actionable: a Retry button (a GET, so a
  // refetch is safe and idempotent).
  if (isError && !data) {
    return (
      <div className="mt-4 text-sm text-neutral-700">
        {t('promptInspector.loadError', "Couldn't load the instructions.")}{' '}
        <button
          type="button"
          onClick={onRetry}
          className="font-semibold text-primary underline-offset-2 hover:underline active:scale-95 motion-reduce:transition-none"
        >
          {t('actions.retry', 'Retry')}
        </button>
      </div>
    )
  }

  // LOADING (no cache) — skeleton, not a spinner-over-blank.
  if (isLoading && !data) {
    return (
      <div className="mt-4 space-y-3" aria-busy="true">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-16 w-full rounded-lg" />
        <Skeleton className="h-16 w-full rounded-lg" />
        <Skeleton className="h-16 w-full rounded-lg" />
      </div>
    )
  }

  if (!data) return null

  return (
    <>
      {/* Every prompt is rendered per turn from the pinned agent version
          (tricorder-session/ADR-0002 Rule 2 — the frozen-prompt regime this
          line used to name is gone). The metadata is the pinned agent and
          version. */}
      <div className="mt-2 shrink-0 space-y-1">
        <p className="text-xs text-neutral-500">
          {t('promptInspector.dynamic', 'Live — rebuilt with the current context on every message')}
        </p>
        <p className="font-mono text-[11px] leading-relaxed text-neutral-400 break-all">
          {data.agent} · v{data.version}
        </p>
      </div>

      {/* Body — the rendered prompt, whole (the preview serves the per-layer
          versions, not per-layer text), then the layer list. */}
      <div className="mt-4 -mx-2 px-2 overflow-y-auto grow">
        <div className="space-y-2.5 pb-2">
          <RenderedPromptCard rendered={data.rendered} />
          {data.layers.map((layer: { name: string; version: string }, i: number) => (

            <div key={`${layer.name}-${i}`} className="flex items-baseline gap-2 rounded-lg border border-neutral-200 px-3 py-2">
              <span className="text-[13px] font-semibold text-neutral-800">{titleCaseLayer(layer.name)}</span>
              <span className="font-mono text-[11px] text-neutral-400">{layer.version}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  )
}

function titleCaseLayer(name: string): string {
  return name.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function RenderedPromptCard({ rendered }: { rendered: string }) {
  return (
    <pre className="whitespace-pre-wrap rounded-lg border border-neutral-200 bg-neutral-50 p-3 text-[12.5px] leading-relaxed text-neutral-800">
      {rendered}
    </pre>
  )
}

